"use client";

import { useEffect, useRef, type MouseEvent as ReactMouseEvent, type ReactNode } from "react";
import { createPortal } from "react-dom";

export type ModalProps = {
  isOpen: boolean;
  onClose?: () => void;
  children: ReactNode;
  /** Overrides the default overlay (backdrop) classes entirely when provided. */
  overlayClassName?: string;
  closeOnBackdropClick?: boolean;
  closeOnEscape?: boolean;
  /** Cycles Tab/Shift+Tab focus within the modal. Default true for new usage; pass false to preserve a pre-existing modal's original (untrapped) tab order during migration. */
  trapFocus?: boolean;
  role?: "dialog" | "alertdialog";
  ariaLabel?: string;
  ariaLabelledBy?: string;
};

const DEFAULT_OVERLAY_CLASSNAME =
  "fixed inset-0 z-modal flex items-center justify-center bg-backdrop p-4 backdrop-blur-sm animate-in fade-in duration-200";

/**
 * Dumb/presentational modal shell: portals its children to document.body so
 * they escape any transformed/overflow-hidden ancestor (e.g. the dashboard
 * shell), and owns only overlay-level concerns (backdrop, focus trap,
 * Escape-to-close, backdrop-click-to-close, stacking). It has no opinion on
 * what's inside — callers keep passing their existing panel markup as
 * children unchanged, and keep owning isOpen/onClose state and all business
 * logic themselves.
 */
export default function Modal({
  isOpen,
  onClose,
  children,
  overlayClassName = DEFAULT_OVERLAY_CLASSNAME,
  closeOnBackdropClick = true,
  closeOnEscape = true,
  trapFocus = true,
  role = "dialog",
  ariaLabel,
  ariaLabelledBy,
}: ModalProps) {
  const overlayRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (closeOnEscape && onClose) {
          e.preventDefault();
          onClose();
        }
        return;
      }

      if (e.key === "Tab" && trapFocus && overlayRef.current) {
        const focusables = overlayRef.current.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (focusables.length === 0) return;

        const firstElement = focusables[0];
        const lastElement = focusables[focusables.length - 1];

        if (e.shiftKey && document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        } else if (!e.shiftKey && document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown, true);
    return () => window.removeEventListener("keydown", handleKeyDown, true);
  }, [isOpen, closeOnEscape, trapFocus, onClose]);

  if (!isOpen) return null;
  if (typeof document === "undefined") return null;

  return createPortal(
    <div
      ref={overlayRef}
      className={overlayClassName}
      role={role}
      aria-modal="true"
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      onClick={(e: ReactMouseEvent<HTMLDivElement>) => {
        if (closeOnBackdropClick && e.target === e.currentTarget && onClose) {
          onClose();
        }
      }}
    >
      {children}
    </div>,
    document.body
  );
}
